if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /**
       * Get queued addon variant IDs from AddonQueueManager
       */
      getQueuedAddonVariantIds() {
        if (typeof window.AddonQueueManager !== 'undefined' && window.AddonQueueManager.isQueueMode()) {
          return window.AddonQueueManager.getQueuedVariantIds();
        }
        return [];
      }

      /**
       * Build items array for cart add (main product + queued addons)
       */
      buildCartItems(mainVariantId, quantity = 1) {
        const items = [{ id: parseInt(mainVariantId), quantity: quantity }];
        
        // Add queued addons
        const queuedAddons = this.getQueuedAddonVariantIds();
        queuedAddons.forEach(variantId => {
          items.push({ id: parseInt(variantId), quantity: 1 });
        });
        
        return items;
      }

      /**
       * Manually update the cart icon bubble when renderContents fails
       */
      updateCartIconBubble(sections) {
        const cartIconBubbleHtml = sections['cart-icon-bubble'];
        if (!cartIconBubbleHtml) return;
        
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(cartIconBubbleHtml, 'text/html');
          const newContent = doc.querySelector('.shopify-section');
          if (newContent) {
            cartIconBubble.innerHTML = newContent.innerHTML;
          }
        }
      }

      /**
       * Safely render cart contents, with fallback for multi-item adds
       */
      safeRenderCartContents(response, hasQueuedAddons) {
        try {
          // For multi-item adds, set the key from the first item (main product)
          if (hasQueuedAddons && response.items && response.items.length > 0) {
            response.key = response.items[0].key;
          }
          
          this.cart.renderContents(response);
        } catch (error) {
          console.warn('Cart notification render failed, updating cart bubble manually:', error);
          // Fallback: update just the cart icon bubble
          if (response.sections) {
            this.updateCartIconBubble(response.sections);
          }
          
          // For cart-notification, still open it to show success even if product details failed
          if (this.cart && this.cart.notification) {
            // The cart icon bubble should be updated, just don't show the product notification
            // The user will see the cart count update
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const mainVariantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        
        // Check for queued addons
        const queuedAddons = this.getQueuedAddonVariantIds();
        const hasQueuedAddons = queuedAddons.length > 0;
        
        // Get sections to render - always include cart-icon-bubble
        let sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
        if (!sections.includes('cart-icon-bubble')) {
          sections.push('cart-icon-bubble');
        }
        
        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        // If we have queued addons, use JSON format to add multiple items
        // Otherwise, use the standard FormData approach
        let fetchPromise;
        
        if (hasQueuedAddons) {
          // Use JSON format for multiple items
          const items = this.buildCartItems(mainVariantId, quantity);
          
          fetchPromise = fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: items,
              sections: sections,
              sections_url: window.location.pathname
            })
          });
        } else {
          // Use standard FormData approach (original behavior)
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          
          if (this.cart) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
          
          fetchPromise = fetch(`${routes.cart_add_url}`, config);
        }

        fetchPromise
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Clear the addon queue and notify addons that main product is now in cart
            if (hasQueuedAddons && typeof window.AddonQueueManager !== 'undefined') {
              window.AddonQueueManager.clearQueue();
              window.AddonQueueManager.setMainProductInCart(true);
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.safeRenderCartContents(response, hasQueuedAddons);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.safeRenderCartContents(response, hasQueuedAddons);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
